import React from 'react';
import { getContext, getDuration, getEasing, isDarkTheme, isAnimationDisabled } from 'common';
import lottie from 'lottie-web';
import _merge from 'lodash/merge';
import { useSpring, animated } from 'react-spring';
import { SPEED_FAST } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_DECELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { SignInContext } from './SignIn';
import Styled, { BREAKPOINT_XS_M } from './styles';

import BG_ANIMATION from './styles/animations/BGLines_960x1080p.json';
import SI_CHIP_ANIMATION from './styles/animations/SI-Chip_960x1080p.json';
import SI_CIRCLE_ANIMATION from './styles/animations/SI-Circle_960x1080p.json';
import SI_LINK_ANIMATION from './styles/animations/SI-Link_960x1080p.json';
import SI_MEMORYCARD_ANIMATION from './styles/animations/SI-MemoryCard_960x1080p.json';
import SI_TRIANGLE_ANIMATION from './styles/animations/SI-Triangle_960x1080p.json';
import CHAT_ANIMATION from './styles/animations/Chat2_960x1080p.json';
import CLOUD_ANIMATION from './styles/animations/Cloud_960x1080p.json';
import CHECK_ANIMATION from './styles/animations/Check_960x1080p.json';
import KNOBS_ANIMATION from './styles/animations/Knobs_960x1080p.json';
import SHIELD_ANIMATION from './styles/animations/Shield_960x1080p.json';

export const ANIMATION_ID = 'ccfk-signin-animation';
const ANIMATION_CLASS = `${ANIMATION_ID}-elements`;
const ANIMATION_OPTIONS = {
  options: {
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid meet'
    }
  }
};
const ANIMATIONS = [
  { data: BG_ANIMATION, id: ANIMATION_ID, options: {}, style: {} },
  { data: CHAT_ANIMATION, id: `${ANIMATION_ID}-chat`, ...ANIMATION_OPTIONS, style: {} },
  {
    data: CLOUD_ANIMATION,
    id: `${ANIMATION_ID}-laptop`,
    ...ANIMATION_OPTIONS,
    style: {}
  },
  { data: SI_CHIP_ANIMATION, id: `${ANIMATION_ID}-chip`, options: {}, style: {} },
  { data: SI_CIRCLE_ANIMATION, id: `${ANIMATION_ID}-circle`, options: {}, style: {} },
  { data: SI_LINK_ANIMATION, id: `${ANIMATION_ID}-link`, options: {}, style: {} },
  { data: SI_MEMORYCARD_ANIMATION, id: `${ANIMATION_ID}-memorycard`, options: {}, style: {} },
  { data: SI_TRIANGLE_ANIMATION, id: `${ANIMATION_ID}-triangle`, options: {}, style: {} },
  { data: CHECK_ANIMATION, id: `${ANIMATION_ID}-check`, ...ANIMATION_OPTIONS, style: {} },
  { data: KNOBS_ANIMATION, id: `${ANIMATION_ID}-knobs`, ...ANIMATION_OPTIONS, style: {} },
  {
    data: SHIELD_ANIMATION,
    id: `${ANIMATION_ID}-shield`,
    ...ANIMATION_OPTIONS,
    style: {} }
];
const ANIMATION_ELEMENTS = ANIMATIONS.map(animation => (
  <div className={ANIMATION_CLASS} id={animation.id} key={animation.id} style={{ ...animation.style }} />
));

const loadAnimations = () => {
  ANIMATIONS.forEach((animation) => {
    lottie.loadAnimation({
      animationData: animation.data,
      // @ts-expect-error: Moe to review
      height: '100%',
      width: '100%',
      loop: false,
      container: document.getElementById(animation.id),
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice'
      },
      ...animation.options
    });
  });
};

export interface SignInBackgroundProps extends React.ComponentPropsWithRef<'div'> {
  /** Place content here. */
  children?: React.ReactNode;
  /** Specify background image source. */
  background?: string;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Control animation config here, used in conjunction with `animateOut` prop of SignIn. This uses React-spring config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To customize the wrapper style. */
  style?: React.CSSProperties
}

/** SignInBackground -- controls background image (or right-aligned content) */
const SignInBackground = React.forwardRef<HTMLElement, SignInBackgroundProps>((props: SignInBackgroundProps, ref) =>
  getContext(({ RTL, theme: globalTheme, disableAnimation }) => {
    const {
      children, background, style, theme, animationProps, disableAnimation: propsdisableAnimation, ...otherProps
    } = props;
    const isMobileView = window.matchMedia(BREAKPOINT_XS_M).matches;
    const resolvedTheme = _merge({}, globalTheme.SIGN_IN, theme);
    const darkTheme = isDarkTheme(globalTheme.NAME);
    const { animateOut } = React.useContext(SignInContext);
    const resolvedDisableAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);
    const springProps = isMobileView
      ? (animationProps || useSpring({
        height: animateOut ? '0%' : '30%',
        delay: animateOut && 1000,
        config: {
          duration: getDuration(SPEED_FAST),
          easing: getEasing(TRANSITION_DECELERATE)
        },
        immediate: resolvedDisableAnimation,
      }))
      : {};

    const resolvedStyles = _merge({}, springProps, style);

    React.useEffect(() => {
      loadAnimations();
    }, [resolvedDisableAnimation]);
    return (
      <Styled.SignInBackground
        $background={background}
        $rtl={RTL}
        ref={ref}
        as={animated.div}
        style={resolvedStyles}
        {...otherProps}
      >
        {!background && !resolvedDisableAnimation && (
          <Styled.SignInBackgroundInner theme={resolvedTheme} dark={darkTheme} style={{ width: '100%', height: '100%' }}>
            {ANIMATION_ELEMENTS}
          </Styled.SignInBackgroundInner>
        )}
        {children}
      </Styled.SignInBackground>
    );
  }));

SignInBackground.displayName = 'SignInBackground';
SignInBackground.defaultProps = {
  children: undefined,
  background: undefined,
  theme: undefined,
  animationProps: undefined,
  disableAnimation: undefined,
  style: {}
};

/* Exports */
export { SignInBackground as default };
